import { useState, useEffect } from "react";
import { API } from "@/App";
import { Instagram, ExternalLink, Heart, MessageCircle, Image as ImageIcon, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

export default function InstagramWidget({ username = "cabvalley", limit = 6 }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    fetchInstagramData();
  }, [username]);

  const fetchInstagramData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Try to fetch from our backend (which can cache Instagram data)
      const res = await safeFetch(`${API}/public/instagram-feed?username=${username}&limit=${limit}`);
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
        setProfileData(data.profile || null);
      } else {
        // Fallback to placeholder data for demo
        setPosts(getPlaceholderPosts());
        setProfileData({
          username: username,
          full_name: "Cab Valley",
          bio: "Premium rides across Jammu, Kashmir & Ladakh 🏔️",
          followers: "2.5K",
          posts_count: 48
        });
      }
    } catch (err) {
      console.error("Failed to fetch Instagram data:", err);
      // Use placeholder for demo
      setPosts(getPlaceholderPosts());
      setProfileData({
        username: username,
        full_name: "Cab Valley",
        bio: "Premium rides across Jammu, Kashmir & Ladakh 🏔️",
        followers: "2.5K",
        posts_count: 48
      });
    }
    setLoading(false);
  };

  const getPlaceholderPosts = () => [
    { id: 1, image: "https://images.unsplash.com/photo-1566837945700-30057527ade0?w=400", likes: 234, comments: 12, caption: "Beautiful morning drive to Gulmarg 🏔️" },
    { id: 2, image: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=400", likes: 189, comments: 8, caption: "Our premium fleet ready for your journey" },
    { id: 3, image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400", likes: 312, comments: 24, caption: "Scenic routes through the valley" },
    { id: 4, image: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400", likes: 156, comments: 6, caption: "Adventure awaits! Book your ride today" },
    { id: 5, image: "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=400", likes: 278, comments: 15, caption: "Winter wonderland trips now available" },
    { id: 6, image: "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?w=400", likes: 198, comments: 9, caption: "Experience Kashmir like never before" },
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-xl flex items-center justify-center">
            <Instagram className="w-5 h-5 text-white" />
          </div>
          <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="aspect-square bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="instagram-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-xl flex items-center justify-center">
            <Instagram className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">@{profileData?.username || username}</h3>
            <p className="text-xs text-slate-500">{profileData?.followers || "0"} followers</p>
          </div>
        </div>
        <a
          href={`https://instagram.com/${username}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-pink-600 hover:text-pink-700 font-medium"
        >
          Follow <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Bio */}
      {profileData?.bio && (
        <p className="text-sm text-slate-600 mb-4">{profileData.bio}</p>
      )}

      {/* Posts Grid */}
      <div className="grid grid-cols-3 gap-2">
        {posts.slice(0, limit).map((post, idx) => (
          <a
            key={post.id || idx}
            href={post.permalink || `https://instagram.com/${username}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative aspect-square rounded-lg overflow-hidden bg-slate-100"
            data-testid={`instagram-post-${idx}`}
          >
            <img
              src={post.image || post.media_url || post.thumbnail_url}
              alt={post.caption || "Instagram post"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="flex items-center gap-3 text-white text-sm">
                <span className="flex items-center gap-1">
                  <Heart className="w-4 h-4 fill-current" /> {post.likes || post.like_count || 0}
                </span>
                <span className="flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" /> {post.comments || post.comments_count || 0}
                </span>
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* View More */}
      <a
        href={`https://instagram.com/${username}`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 block w-full text-center py-2.5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
      >
        View More on Instagram
      </a>
    </div>
  );
}
